import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  TextField,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import {
  addInventoryItems,
  getInventoryData,
  updateInventoryItem,
  deleteInventoryItem,
  saveVolumne,
  getMoveService,
  updateMoveService,
} from "../../redux/action/move-service";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { setStep } from "../../redux/reducers/stepSlice";
import { useNavigate, useParams } from "react-router-dom";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import Header from "../header/Header";

interface InventoryItem {
  id?: string;
  name: string;
  width: string;
  length: string;
  height: string;
  roomName?: string;
  imageUrl?: string;
  originalItem?: any;
  count?: number;
}

const Inventory = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { businessName, canonicalId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [carrier, setCarrier] = useState<any>(null);
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [newItem, setNewItem] = useState<InventoryItem>({
    name: "",
    width: "",
    length: "",
    height: "",
  });
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null);
  const lastStep = 9;
const {carrierCanonicalId} =useParams()
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (editingItem) {
      setEditingItem({ ...editingItem, [name]: value });
    } else {
      setNewItem({ ...newItem, [name]: value });
    }
  };
  useEffect(() => {
    const updateLastStep = async () => {
        const moveServiceData = {
          canonical_id: canonicalId,
          last_step: lastStep,
        };
        await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res:any)=>{
        });
    };
  
    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  useEffect(() => {
    const fetchInventory = async () => {
      const sessionId = localStorage.getItem("SessionId");
      if (!sessionId) {
        setError("No session ID found");
        setLoading(false);
        return;
      }

      try {
        const result = await dispatch<any>(getInventoryData(sessionId));
        const response = unwrapResult(result);
        if (response?.items) {
          setItems(
            response.items.map((item: any) => ({
              ...item,
              id: `${item.name}-${Math.random().toString(36).substr(2, 9)}`,
            }))
          );
        } else {
          setItems([
            {
              name: "Bed",
              width: "5.8",
              length: "6.7",
              height: "3.0",
              id: "bed-1",
            },
            {
              name: "Table",
              width: "5.8",
              length: "6.7",
              height: "3.0",
              id: "table-1",
            },
          ]);
        }
      } catch (err) {
        setError("Failed to load inventory data");
        setItems([
          {
            name: "Bed",
            width: "5.8",
            length: "6.7",
            height: "3.0",
            id: "bed-1",
          },
          {
            name: "Table",
            width: "5.8",
            length: "6.7",
            height: "3.0",
            id: "table-1",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [dispatch]);

  useEffect(() => {
    if (businessName) {
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrier(res);
        })
        .catch((err: any) => {
          console.log(err);
        });
    }
  }, [businessName]);

  const handleAddItem = async () => {
    if (newItem.name && newItem.width && newItem.length && newItem.height) {
      try {
        setIsLoading(true);
        setLoadingMessage("Adding item...");
        const sessionId = localStorage.getItem("SessionId");
        if (!sessionId) {
          throw new Error("No session ID found");
        }
        const data = {
          name: newItem.name,
          width: newItem.width,
          length: newItem.length,
          height: newItem.height,
          sessionId,
        };
        const result = await dispatch<any>(addInventoryItems(data));
        const response = unwrapResult(result);
        setItems(
          response.items.map((item: any) => ({
            ...item,
            id: `${item.name}-${Math.random().toString(36).substr(2, 9)}`,
          }))
        );
        setNewItem({ name: "", width: "", length: "", height: "" });
        setShowAddItemForm(false);
      } catch (error) {
        console.error("Failed to save item:", error);
        alert("Failed to save item. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("Please fill in all fields.");
    }
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem({
      ...item,
      originalItem: {
        name: item.name,
        width: item.width,
        length: item.length,
        height: item.height,
      },
    });
  };

  const handleUpdateItem = async () => {
    if (
      editingItem &&
      editingItem.name &&
      editingItem.width &&
      editingItem.length &&
      editingItem.height
    ) {
      try {
        setIsLoading(true);
        setLoadingMessage("Updating item...");
        const sessionId = localStorage.getItem("SessionId");
        if (!sessionId) {
          throw new Error("No session ID found");
        }
        const formatDimension = (dim: string) => {
          const num = parseFloat(dim);
          return isNaN(num) ? "0" : num.toString();
        };

        const updateData = {
          originalItem: {
            name: editingItem.originalItem?.name || editingItem.name,
            width: formatDimension(
              editingItem.originalItem?.width || editingItem.width
            ),
            length: formatDimension(
              editingItem.originalItem?.length || editingItem.length
            ),
            height: formatDimension(
              editingItem.originalItem?.height || editingItem.height
            ),
          },
          updatedItem: {
            name: editingItem.name,
            width: formatDimension(editingItem.width),
            length: formatDimension(editingItem.length),
            height: formatDimension(editingItem.height),
          },
          sessionId,
        };

        const result = await dispatch<any>(updateInventoryItem(updateData));
        const response = unwrapResult(result);

        setItems(
          items.map((item) =>
            item.id === editingItem.id
              ? {
                  ...response.item,
                  id: editingItem.id,
                  width: response.item.width.toString(),
                  length: response.item.length.toString(),
                  height: response.item.height.toString(),
                }
              : item
          )
        );

        setEditingItem(null);
      } catch (error) {
        console.error("Failed to update item:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("Please fill in all fields.");
    }
  };

  const handleDeleteClick = (item: InventoryItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setIsLoading(true);
        setLoadingMessage("Deleting item...");
        const sessionId = localStorage.getItem("SessionId");
        if (!sessionId) {
          throw new Error("No session ID found");
        }

        const deleteData = {
          item: itemToDelete,
          sessionId,
        };

        await dispatch<any>(deleteInventoryItem(deleteData));
        setItems(items.filter((item) => item.id !== itemToDelete.id));
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } catch (error) {
        console.error("Failed to delete item:", error);
        alert("Failed to delete item. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleNext = async() => {
    try {
    setIsLoading(true);
    setLoadingMessage("Saving inventory...");
    dispatch<any>(saveVolumne(canonicalId || ""))
      .then(unwrapResult)
      .then((res: any) => {
        setTimeout(() => {
          const offersInsurance = carrier?.insurance != null;
          if (offersInsurance) {
            navigate(`/${carrier.business_name}/${carrierCanonicalId}/insurance/${canonicalId}`);
          } else {
            navigate(`/${carrier.business_name}/${carrierCanonicalId}/move-price/${canonicalId}`);
          }
        }, 1000);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
      
    } catch (error) {
      console.error(error)
    }
  };

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ textAlign: "center", padding: 4 }}>
        <Typography color="error">{error}</Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <div>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              overflowY: "auto",
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "16px" : "18px"}
              textAlign="center"
            >
              Inventory
            </Typography>
            <Button
              variant="contained"
              sx={{
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: "8px 20px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#357ABD" },
              }}
              onClick={() => {
                setShowAddItemForm(!showAddItemForm);
                setEditingItem(null);
              }}
            >
              {editingItem
                ? "Cancel Edit"
                : showAddItemForm
                ? "Cancel"
                : "Add Item"}
            </Button>
          </Box>
          <Divider sx={{ background: "#D3D3D3", mb: 5 }} />

          {(showAddItemForm || editingItem) && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Item Name"
                name="name"
                value={editingItem ? editingItem.name : newItem.name}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Width"
                  name="width"
                  value={editingItem ? editingItem.width : newItem.width}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label="Length"
                  name="length"
                  value={editingItem ? editingItem.length : newItem.length}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label="Height"
                  name="height"
                  value={editingItem ? editingItem.height : newItem.height}
                  onChange={handleInputChange}
                />
              </Box>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "20px",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  padding: "8px",
                  textTransform: "none",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "600",
                  "&:hover": { backgroundColor: "#357ABD" },
                }}
                onClick={editingItem ? handleUpdateItem : handleAddItem}
              >
                {editingItem ? "Update Item" : "Save Item"}
              </Button>
            </Box>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: theme.palette.primary.main,
                    color: "#fff",
                  }}
                >
                  <th style={{ padding: "8px", textAlign: "left" }}>
                    Item Name
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>
                    Width (ft)
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>
                    Length (ft)
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>
                    Height (ft)
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.flatMap((item, index) =>
                  Array.from({ length: item.count || 1 }).map(
                    (_, countIndex) => (
                      <tr
                        key={`${index}-${countIndex}`}
                        style={{ textAlign: "left" }}
                      >
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #E1EFFF",
                          }}
                        >
                          {item.name}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #E1EFFF",
                          }}
                        >
                          {item.width}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #E1EFFF",
                          }}
                        >
                          {item.length}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #E1EFFF",
                          }}
                        >
                          {item.height}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            borderBottom: "1px solid #E1EFFF",
                          }}
                        >
                          <IconButton
                            onClick={() => handleEditItem(item)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(item)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 10px",
              alignItems: "center",
              backgroundColor: theme.palette.primary.main,
              mt: 2,
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "14px" : "16px"}
              color="#fff"
            >
              Total Items
            </Typography>
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "14px" : "16px"}
              color="#fff"
            >
              {items.reduce((total, item) => total + (item.count || 1), 0)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: "100%", paddingBottom: "20px", paddingTop: "20px" }}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: "8px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
            }}
            onClick={handleNext}
          >
            Continue
          </Button>
        </Box>
      </Container>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemToDelete?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Inventory;
